import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { signInWithEmailLink, isSignInWithEmailLink } from 'firebase/auth';
import { doc, getDoc, updateDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig'; // Adjust path as needed
import { LuLoader, LuCheck, LuMail, LuAlertCircle } from 'react-icons/lu';

const AcceptInvite = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [inviteData, setInviteData] = useState(null);
  const [email, setEmail] = useState('');
  const [step, setStep] = useState('verifying'); // verifying, email-input, processing, complete

  useEffect(() => {
    handleInviteAcceptance();
  }, []);

  const handleInviteAcceptance = async () => {
    try {
      // Check if this is a sign-in with email link
      if (isSignInWithEmailLink(auth, window.location.href)) {
        await handleEmailLinkSignIn();
      } else {
        // Handle direct invite link
        await handleDirectInvite();
      }
    } catch (error) {
      console.error('Error handling invite:', error);
      setError('Failed to process invite. Please try again.');
      setLoading(false);
    }
  };

  const handleEmailLinkSignIn = async () => {
    // Get email from localStorage or prompt user
    let emailForSignIn = window.localStorage.getItem('emailForSignIn');

    if (!emailForSignIn) {
      setStep('email-input');
      setLoading(false);
      return;
    }

    try {
      setStep('processing');

      // Sign in with email link
      const result = await signInWithEmailLink(auth, emailForSignIn, window.location.href);

      // Get invite data from URL params or localStorage
      const inviteToken = searchParams.get('inviteToken');
      const institutionId = searchParams.get('institutionId');
      const moduleId = searchParams.get('moduleId');

      let storedInviteData = null;
      try {
        storedInviteData = JSON.parse(window.localStorage.getItem(`inviteData_${emailForSignIn}`));
      } catch (e) {
        console.warn('Could not parse stored invite data');
      }

      if (inviteToken || storedInviteData) {
        await processInvite(result.user, inviteToken || storedInviteData?.inviteToken, {
          institutionId: institutionId || storedInviteData?.institutionId,
          moduleId: moduleId || storedInviteData?.moduleId,
          moduleName: storedInviteData?.moduleName,
          role: storedInviteData?.role || 'member'
        });
      } else {
        throw new Error('Invite data not found');
      }

      // Clean up localStorage
      window.localStorage.removeItem('emailForSignIn');
      window.localStorage.removeItem(`inviteData_${emailForSignIn}`);

    } catch (error) {
      console.error('Error signing in with email link:', error);
      setError('Failed to sign in. Please try again or contact support.');
      setLoading(false);
    }
  };

  const handleDirectInvite = async () => {
    const inviteToken = searchParams.get('inviteToken');

    if (!inviteToken) {
      setError('Invalid invite link. Please check the link and try again.');
      setLoading(false);
      return;
    }

    try {
      // Find invite in Firestore
      const invitesRef = collection(db, 'invites');
      const q = query(invitesRef, where('inviteToken', '==', inviteToken));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError('Invite not found or has expired.');
        setLoading(false);
        return;
      }

      const inviteDoc = querySnapshot.docs[0];
      const invite = { id: inviteDoc.id, ...inviteDoc.data() };

      // Check if invite is still valid
      if (invite.expiresAt.toDate() < new Date()) {
        setError('This invite has expired. Please request a new invitation.');
        setLoading(false);
        return;
      }

      if (invite.status === 'accepted') {
        setError('This invite has already been used.');
        setLoading(false);
        return;
      }

      setInviteData(invite);

      if (auth.currentUser) {
        await processInvite(auth.currentUser, inviteToken, {
          institutionId: invite.institutionId,
          moduleId: invite.moduleId,
          moduleName: invite.moduleName,
          role: invite.role
        });
      } else {
        // User needs to sign in/up - redirect to auth with invite context
        window.localStorage.setItem('pendingInvite', JSON.stringify({
          inviteToken,
          institutionId: invite.institutionId,
          moduleId: invite.moduleId,
          moduleName: invite.moduleName,
          role: invite.role
        }));
        navigate(`/auth?redirect=${encodeURIComponent(window.location.href)}`);
      }

    } catch (error) {
      console.error('Error processing direct invite:', error);
      setError('Failed to process invite. Please try again.');
      setLoading(false);
    }
  };

  const processInvite = async (user, inviteToken, inviteInfo) => {
    try {
      setStep('processing');

      // Add user to institution
      const userInstitutionRef = doc(db, 'colleges', inviteInfo.institutionId, 'members', user.uid);
      await setDoc(userInstitutionRef, {
        userId: user.uid,
        email: user.email,
        displayName: user.displayName || user.email,
        role: inviteInfo.role,
        joinedAt: new Date(),
        joinedVia: 'invite',
        inviteToken: inviteToken
      }, { merge: true });

      // Add user to module if specified
      if (inviteInfo.moduleId) {
        const userModuleRef = doc(db, 'colleges', inviteInfo.institutionId, 'configs', inviteInfo.moduleId, 'members', user.uid);
        await setDoc(userModuleRef, {
          userId: user.uid,
          email: user.email,
          displayName: user.displayName || user.email,
          role: inviteInfo.role,
          joinedAt: new Date(),
          joinedVia: 'invite'
        }, { merge: true });
      }

      // Update invite status
      const invitesRef = collection(db, 'invites');
      const q = query(invitesRef, where('inviteToken', '==', inviteToken));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const inviteDoc = querySnapshot.docs[0];
        await updateDoc(doc(db, 'invites', inviteDoc.id), {
          status: 'accepted',
          acceptedAt: new Date(),
          acceptedBy: user.uid
        });
      }

      setStep('complete');
      setSuccess(`Successfully joined ${inviteInfo.moduleName || 'the organization'}!`);

      // Redirect to the module or dashboard after a short delay
      setTimeout(() => {
        navigate(`/dashboard/${inviteInfo.moduleId || ''}`, { replace: true });
      }, 2000);

    } catch (error) {
      console.error('Error processing invite:', error);
      setError('Failed to join organization. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Sign in with email link using the provided email
      await signInWithEmailLink(auth, email.trim(), window.location.href);

      // Continue with invite processing
      await handleEmailLinkSignIn();

    } catch (error) {
      console.error('Error with email sign in:', error);
      setError('Failed to verify email. Please check your email address and try again.');
      setLoading(false);
    }
  };

  const renderContent = () => {
    switch (step) {
      case 'email-input':
        return (
          <div className="text-center space-y-6">
            <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto">
              <LuMail className="w-8 h-8 text-blue-400" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-white mb-2">Verify Your Email</h2>
              <p className="text-gray-400">Please enter the email address that received the invitation.</p>
            </div>

            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div className="relative">
                <LuMail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@company.com"
                  className="w-full pl-10 pr-4 py-3 bg-[#ffffff08] border border-[#ffffff15] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 focus:bg-[#ffffff12] transition-all duration-200"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-600 disabled:to-gray-600 disabled:cursor-not-allowed text-white rounded-xl font-medium transition-all duration-200"
              >
                {loading ? 'Verifying...' : 'Continue'}
              </button>
            </form>
          </div>
        );

      case 'processing':
        return (
          <div className="text-center space-y-6">
            <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto">
              <LuLoader className="w-8 h-8 text-blue-400 animate-spin" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-white mb-2">Processing Invitation</h2>
              <p className="text-gray-400">Please wait while we set up your access...</p>
            </div>
          </div>
        );

      case 'complete':
        return (
          <div className="text-center space-y-6">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
              <LuCheck className="w-8 h-8 text-green-400" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-white mb-2">Welcome!</h2>
              <p className="text-gray-400">You've successfully joined the organization. Redirecting...</p>
            </div>
          </div>
        );

      default: // verifying
        return (
          <div className="text-center space-y-6">
            <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto">
              <LuLoader className="w-8 h-8 text-blue-400 animate-spin" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-white mb-2">Verifying Invitation</h2>
              <p className="text-gray-400">Please wait while we verify your invitation...</p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center p-4">
      <div className="bg-[#0a0a0a] border border-[#ffffff15] rounded-2xl w-full max-w-md p-8 shadow-2xl">
        {renderContent()}

        {error && (
          <div className="mt-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start space-x-3">
            <LuAlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="mt-6 p-4 bg-green-500/10 border border-green-500/20 rounded-xl flex items-start space-x-3">
            <LuCheck className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
            <p className="text-green-400 text-sm">{success}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcceptInvite;